import {
  Flex,
  Title,
  Text,
  Image,
  Stack,
  Card,
  Button,
  Space,
} from '@mantine/core';
import { useTranslation } from 'react-i18next';
import classes from '../../../app.module.css';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import ReportGmailerrorredOutlinedIcon from '@mui/icons-material/ReportGmailerrorredOutlined';
import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined';
import { ParameterLevel } from '@web/common';

export interface categoryMessage {
  parameter: string;
  level: ParameterLevel;
}

export enum RiskLevel {
  LOW = 'low',
  MODERATE = 'mid',
  HIGH = 'high',
}

export const CategorySummary = ({
  title,
  level,
  risk,
  messages,
  sectionLink,
  imageUrl,
}: {
  title: string;
  level: ParameterLevel;
  risk: RiskLevel;
  imageUrl: string;
  sectionLink: () => void;
  messages?: categoryMessage[];
}) => {
  const { t } = useTranslation(['common', 'reports']);

  const backgroundStyle = () => {
    switch (risk) {
      case RiskLevel.LOW: {
        return {
          background:
            'linear-gradient(90deg,rgba(229, 243, 255, 1) 46%, rgba(194, 225, 251, 1) 100%)',
        };
      }
      case RiskLevel.MODERATE: {
        return {
          background:
            'linear-gradient(90deg,rgba(229, 243, 255, 1) 65%, rgba(242, 204, 234, 1) 100%)',
        };
      }
      case RiskLevel.HIGH: {
        return {
          background:
            'linear-gradient(90deg,rgba(247, 236, 248, 1) 46%, rgba(242, 204, 234, 1) 100%)',
        };
      }
      default: {
        return {
          background:
            'linear-gradient(90deg,rgba(229, 243, 255, 1) 46%, rgba(194, 225, 251, 1) 100%)',
        };
      }
    }
  };

  const mappedMessages = () => {
    if (!messages)
      return (
        <Flex direction={'row'} gap={'var(--mantine-spacing-xs)'}>
          <CheckCircleOutlineOutlinedIcon style={{ fontSize: 21 }} />
          <Text style={{ lineHeight: 1.3, color: '#000' }}>
            {t('reports:category_all_normal')}
          </Text>
        </Flex>
      );
    return messages.map((entry) => {
      return (
        <Flex direction={'row'} gap={'var(--mantine-spacing-xs)'}>
          <ReportGmailerrorredOutlinedIcon
            style={{
              fontSize: 21,
              fill: 'var(--mantine-color-innovationPurple-0)',
            }}
          />
          <Text
            style={{
              lineHeight: 1.3,
              color: 'var(--mantine-color-innovationPurple-0)',
            }}
          >
            <b>{entry.parameter}</b>&nbsp;{t(`reports:${entry.level}`)}
          </Text>
        </Flex>
      );
    });
  };

  return (
    <Card className={classes.reportCategorySummariesCard}>
      <Card.Section
        className={classes.categoryCardTopSection}
        style={backgroundStyle()}
      >
        <Flex direction="row">
          <Flex
            style={{ width: 'fit-content' }}
            direction={'column'}
            justify={'center'}
          >
            <Image src={imageUrl} />
          </Flex>
          <Flex direction="column">
            <Title order={3} style={{ lineHeight: 1 }}>
              {title}
            </Title>
            <Text>{t(`reports:${level}`)}</Text>
          </Flex>
        </Flex>
      </Card.Section>
      <Card.Section className={classes.categoryCardBottomSection}>
        <Stack gap={'var(--mantine-spacing-xs)'}>{mappedMessages()}</Stack>
        <Space style={{ height: 0, flexGrow: 1 }} />
        <Button variant="inline-text" fullWidth onClick={sectionLink}>
          <KeyboardArrowDownOutlinedIcon
            style={{ fill: 'var(--mantine-color-otherGrey60-0)' }}
          />
        </Button>
      </Card.Section>
    </Card>
  );
};
